import type { ApiErrorCode } from '#root/enums/api'

/**
 * Wraps an async operation with a single delayed retry. If `fn` throws an `ApiError` whose
 * `errorId` is **not** in `expectedErrorCodes`, waits `time` ms and invokes `fn` once more.
 * Errors whose code matches `expectedErrorCodes` are re-thrown immediately.
 * @template T - Resolution type of `fn`.
 * @param fn - The operation to attempt.
 * @param time - Delay in milliseconds before the retry.
 * @param expectedErrorCodes - Error codes that should bubble up without a retry.
 * @returns The eventual resolution of `fn`.
 * @throws The wrapped `ApiError` when its code is in `expectedErrorCodes`, or the second-attempt error.
 * @category Utils
 * @remarks Will be removed once https://github.com/unjs/ofetch/issues/358 ships.
 */
export async function withRetryAfterTime<T = unknown>(
  fn: () => Promise<T>,
  time: number,
  expectedErrorCodes: ApiErrorCode[] = []
): Promise<T> {
  try {
    return await fn()
  }
  catch (err) {
    assertApiError(err)
    // if no specific error code given or error code matches given one, retry after given time
    if (!expectedErrorCodes.includes(err.errorId)) {
      await new Promise((resolve) => setTimeout(resolve, time))
      return await fn()
    }

    throw err
  }
}
